import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { errorToast } from "@telluscompany/design-system";

import { useLocalization } from "@i18n/hooks/useLocalization";
import { axiosPatchData } from "@shared/api/clients";
import { queryClient } from "@/shared/config";
import type { User } from "@/shared/api/server/gs3Server";

export type PatchUserPreferencesResponse = {
  userPreferences: Record<string, unknown>;
};

type ErrorToastNotifier = (options: Parameters<typeof errorToast>[0]) => void;

export const userPreferencesErrorToast = {
  notify: errorToast,
};

const notifyPreferenceError: ErrorToastNotifier = (options) => {
  userPreferencesErrorToast.notify(options);
};

export const patchUserPreferences = async (
  preferences: Record<string, unknown>,
): Promise<PatchUserPreferencesResponse> =>
  axiosPatchData<
    PatchUserPreferencesResponse,
    { preferences: Record<string, unknown> }
  >("v1/user/me/preferences", { preferences });

export const usePatchUserPreferencesMutation = (
  notifyError: ErrorToastNotifier = notifyPreferenceError,
) => {
  const { translate } = useLocalization();

  return useMutation({
    mutationFn: async (preferences: Record<string, unknown>) => {
      try {
        return await patchUserPreferences(preferences);
      } catch (error) {
        const axiosError = error as AxiosError;
        notifyError({
          description:
            axiosError.parsedMessage ?? translate("common.errorEditing"),
        });
        throw error;
      }
    },
    onSuccess: ({ userPreferences }) => {
      queryClient.setQueryData<User>(["me"], (current) => {
        if (!current) {
          return current;
        }

        return {
          ...current,
          userPreferences,
        };
      });
    },
  });
};
